import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { trim } from 'lodash';
import { FONT_FAMILY_EXPORT_MAP } from '../constants';

const FONT_FAMILIES = {
  arial: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  helvetica: 'Helvetica, "Helvetica Neue", Arial, sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
  timesNewRoman: '"Times New Roman", Times, serif',
  courierNew: '"Courier New", Courier, monospace',
  verdana: 'Verdana, Geneva, sans-serif',
  trebuchetMs: '"Trebuchet MS", Helvetica, sans-serif',
} as const;

const options = {
  defaultBlockTag: 'div',
  entityStyleFn: (entity) => {
    const entityType = entity.get('type');
    if (entityType === 'link') {
      const data = entity.getData();
      return {
        element: 'a',
        attributes: {
          href: data.src,
        },
      };
    }

    if (entityType === 'variable') {
      const data = entity.getData();

      return {
        element: 'span',
        attributes: {
          data: data.data.field,
        },
      };
    }

    if (entityType === 'IMAGE' || entityType === 'image') {
      const data = entity.getData();

      // Build style string with forced dimensions for email clients
      // Gmail and other email clients ignore width/height attributes, so we use inline CSS
      let styleString =
        'display: block; max-width: 100%; border: 0; outline: none; text-decoration: none; vertical-align: top;';

      // Force dimensions using inline CSS - email clients respect this better
      if (data.width && typeof data.width === 'number') {
        styleString += ` width: ${data.width}px !important; max-width: ${data.width}px !important;`;
      }
      if (data.height && typeof data.height === 'number') {
        styleString += ` height: ${data.height}px !important;`;
      }

      // Add alignment styles with email-friendly approach
      if (data.alignment === 'center') {
        styleString += ' margin: 0 auto !important; text-align: center;';
      } else if (data.alignment === 'right') {
        styleString += ' margin-left: auto !important; text-align: right;';
      } else {
        styleString += ' text-align: left;';
      }

      // Add email-specific compatibility styles
      styleString +=
        ' -webkit-user-drag: none; -khtml-user-drag: none; -moz-user-drag: none; -o-user-drag: none; user-drag: none;';
      styleString += ' mso-line-height-rule: exactly; -ms-interpolation-mode: bicubic;';

      const attributes: { [key: string]: string } = {
        src: data.src,
        alt: data.alt || '',
        style: styleString,
      };

      // Add width/height attributes for maximum compatibility
      if (data.width && typeof data.width === 'number') {
        attributes.width = data.width.toString();
      }
      if (data.height && typeof data.height === 'number') {
        attributes.height = data.height.toString();
      }

      // Add data-alignment attribute to preserve alignment during post-processing
      if (data.alignment) {
        attributes['data-alignment'] = data.alignment;
      }

      return {
        element: 'img',
        attributes,
      };
    }

    if (entityType === 'VIDEO' || entityType === 'video') {
      const data = entity.getData();

      // Create a unique placeholder that we'll replace in post-processing
      const videoId = `video_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const placeholder = `VIDEO_PLACEHOLDER_${videoId}_DATA_${JSON.stringify(data)}_END`;

      return {
        element: 'span',
        attributes: {
          'data-video-placeholder': placeholder
        }
      };
    }

    if (entityType === 'CTA_BUTTON') {
      const data = entity.getData();

      // Create a unique placeholder that we'll replace in post-processing
      const ctaId = `cta_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const placeholder = `CTA_PLACEHOLDER_${ctaId}_DATA_${JSON.stringify(data)}_END`;

      return {
        element: 'span',
        attributes: {
          'data-cta-placeholder': placeholder
        }
      };
    }

    if (entityType === 'TABLE') {
      const data = entity.getData();

      // Create a unique placeholder that we'll replace in post-processing
      const tableId = `table_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const placeholder = `TABLE_PLACEHOLDER_${tableId}_DATA_${JSON.stringify(data)}_END`;

      return {
        element: 'span',
        attributes: {
          'data-table-placeholder': placeholder
        }
      };
    }

    if (entityType === 'DIVIDER') {
      const data = entity.getData();

      // Create a unique placeholder that we'll replace in post-processing
      const dividerId = `divider_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const placeholder = `DIVIDER_PLACEHOLDER_${dividerId}_DATA_${JSON.stringify(data)}_END`;

      return {
        element: 'span',
        attributes: {
          'data-divider-placeholder': placeholder
        }
      };
    }
  },
  blockRenderers: {
    unstyled: (block: any) => {
      if (!trim(block.getText()).length) {
        return '<br />';
      }
    },
    blockquote: (block: any) => {
      const text = block.getText();
      // Email-friendly blockquote styles
      const blockquoteStyle = [
        'border-left: 4px solid #d1d5db',
        'margin: 16px 0',
        'padding: 12px 16px',
        'background-color: #f9fafb',
        'font-style: italic',
        'color: #4b5563'
      ].join('; ');

      return `<blockquote style="${blockquoteStyle}">${text}</blockquote>`;
    },
  },
  inlineStyles: {
    // Font family styles - match Editor component style names
    FONT_FAMILY_ARIAL: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.arial};` },
    },
    FONT_FAMILY_HELVETICA: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.helvetica};` },
    },
    FONT_FAMILY_GEORGIA: { element: 'span', attributes: { style: `font-family: ${FONT_FAMILIES.georgia};` } },
    FONT_FAMILY_TIMES_NEW_ROMAN: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.timesNewRoman};` },
    },
    FONT_FAMILY_COURIER_NEW: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.courierNew};` },
    },
    FONT_FAMILY_VERDANA: { element: 'span', attributes: { style: `font-family: ${FONT_FAMILIES.verdana};` } },
    FONT_FAMILY_TREBUCHET_MS: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.trebuchetMs};` },
    },

    // Font size styles - match Editor component style names
    FONT_SIZE_12: { element: 'span', attributes: { style: 'font-size: 12px;' } },
    FONT_SIZE_14: { element: 'span', attributes: { style: 'font-size: 14px;' } },
    FONT_SIZE_16: { element: 'span', attributes: { style: 'font-size: 16px;' } },
    FONT_SIZE_18: { element: 'span', attributes: { style: 'font-size: 18px;' } },
    FONT_SIZE_20: { element: 'span', attributes: { style: 'font-size: 20px;' } },
    FONT_SIZE_24: { element: 'span', attributes: { style: 'font-size: 24px;' } },
    FONT_SIZE_28: { element: 'span', attributes: { style: 'font-size: 28px;' } },
    FONT_SIZE_32: { element: 'span', attributes: { style: 'font-size: 32px;' } },

    // Text color styles - match Editor component style names
    TEXT_COLOR_000000: { element: 'span', attributes: { style: 'color: #000000;' } },
    TEXT_COLOR_333333: { element: 'span', attributes: { style: 'color: #333333;' } },
    TEXT_COLOR_666666: { element: 'span', attributes: { style: 'color: #666666;' } },
    TEXT_COLOR_999999: { element: 'span', attributes: { style: 'color: #999999;' } },
    TEXT_COLOR_CCCCCC: { element: 'span', attributes: { style: 'color: #CCCCCC;' } },
    TEXT_COLOR_FFFFFF: { element: 'span', attributes: { style: 'color: #FFFFFF;' } },
    TEXT_COLOR_FF0000: { element: 'span', attributes: { style: 'color: #FF0000;' } },
    TEXT_COLOR_FF8000: { element: 'span', attributes: { style: 'color: #FF8000;' } },
    TEXT_COLOR_FFFF00: { element: 'span', attributes: { style: 'color: #FFFF00;' } },
    TEXT_COLOR_80FF00: { element: 'span', attributes: { style: 'color: #80FF00;' } },
    TEXT_COLOR_00FF00: { element: 'span', attributes: { style: 'color: #00FF00;' } },
    TEXT_COLOR_00FF80: { element: 'span', attributes: { style: 'color: #00FF80;' } },
    TEXT_COLOR_00FFFF: { element: 'span', attributes: { style: 'color: #00FFFF;' } },
    TEXT_COLOR_0080FF: { element: 'span', attributes: { style: 'color: #0080FF;' } },
    TEXT_COLOR_0000FF: { element: 'span', attributes: { style: 'color: #0000FF;' } },
    TEXT_COLOR_8000FF: { element: 'span', attributes: { style: 'color: #8000FF;' } },
    TEXT_COLOR_FF00FF: { element: 'span', attributes: { style: 'color: #FF00FF;' } },
    TEXT_COLOR_FF0080: { element: 'span', attributes: { style: 'color: #FF0080;' } },

    // Highlight/background color styles - match Editor component style names
    HIGHLIGHT_COLOR_000000: { element: 'span', attributes: { style: 'background-color: #000000;' } },
    HIGHLIGHT_COLOR_333333: { element: 'span', attributes: { style: 'background-color: #333333;' } },
    HIGHLIGHT_COLOR_666666: { element: 'span', attributes: { style: 'background-color: #666666;' } },
    HIGHLIGHT_COLOR_999999: { element: 'span', attributes: { style: 'background-color: #999999;' } },
    HIGHLIGHT_COLOR_CCCCCC: { element: 'span', attributes: { style: 'background-color: #CCCCCC;' } },
    HIGHLIGHT_COLOR_FFFFFF: { element: 'span', attributes: { style: 'background-color: #FFFFFF;' } },
    HIGHLIGHT_COLOR_FF0000: { element: 'span', attributes: { style: 'background-color: #FF0000;' } },
    HIGHLIGHT_COLOR_FF8000: { element: 'span', attributes: { style: 'background-color: #FF8000;' } },
    HIGHLIGHT_COLOR_FFFF00: { element: 'span', attributes: { style: 'background-color: #FFFF00;' } },
    HIGHLIGHT_COLOR_80FF00: { element: 'span', attributes: { style: 'background-color: #80FF00;' } },
    HIGHLIGHT_COLOR_00FF00: { element: 'span', attributes: { style: 'background-color: #00FF00;' } },
    HIGHLIGHT_COLOR_00FF80: { element: 'span', attributes: { style: 'background-color: #00FF80;' } },
    HIGHLIGHT_COLOR_00FFFF: { element: 'span', attributes: { style: 'color: #00FFFF;' } },
    HIGHLIGHT_COLOR_0080FF: { element: 'span', attributes: { style: 'background-color: #0080FF;' } },
    HIGHLIGHT_COLOR_0000FF: { element: 'span', attributes: { style: 'background-color: #0000FF;' } },
    HIGHLIGHT_COLOR_8000FF: { element: 'span', attributes: { style: 'background-color: #8000FF;' } },
    HIGHLIGHT_COLOR_FF00FF: { element: 'span', attributes: { style: 'background-color: #FF00FF;' } },
    HIGHLIGHT_COLOR_FF0080: { element: 'span', attributes: { style: 'background-color: #FF0080;' } },
    HIGHLIGHT_NONE: { element: 'span', attributes: { style: 'background-color: transparent;' } },

    // Text alignment styles - match Editor component style names
    ALIGN_CENTER: { element: 'div', attributes: { style: 'text-align: center; display: block;' } },
    ALIGN_RIGHT: { element: 'div', attributes: { style: 'text-align: right; display: block;' } },
    ALIGN_JUSTIFY: { element: 'div', attributes: { style: 'text-align: justify; display: block;' } },

    // Additional inline styles
    STRIKETHROUGH: { element: 'span', attributes: { style: 'text-decoration: line-through;' } },
    CODE: {
      element: 'code',
      attributes: { style: 'background-color: #f1f1f1; padding: 2px 4px; border-radius: 3px; font-family: monospace;' },
    },
  },
  customStyleFn: (style: any) => {
    // Handle dynamic colors that might be added at runtime
    if (style.startsWith('TEXT_COLOR_')) {
      const color = style.replace('TEXT_COLOR_', '');
      return { style: `color: #${color};` };
    }

    if (style.startsWith('HIGHLIGHT_COLOR_')) {
      const color = style.replace('HIGHLIGHT_COLOR_', '');
      return { style: `background-color: #${color};` };
    }

    if (style.startsWith('FONT_FAMILY_')) {
      const fontFamily = style.replace('FONT_FAMILY_', '');
      if (FONT_FAMILY_EXPORT_MAP[fontFamily]) {
        return { style: `font-family: ${FONT_FAMILY_EXPORT_MAP[fontFamily]};` };
      }
    }

    if (style.startsWith('FONT_SIZE_')) {
      const size = style.replace('FONT_SIZE_', '');
      return { style: `font-size: ${size}px;` };
    }

    return {};
  },
};

import { imageResizeStore } from './imageResizeStore';

function escapeHtmlText(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtmlAttr(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function generateTableHTML(tableData: any): string {
  const { rows, alignment, borderStyle, headerRow, width } = tableData;

  // Container alignment styles
  let containerStyle = 'margin: 16px 0;';
  if (alignment === 'center') {
    containerStyle += ' text-align: center;';
  } else if (alignment === 'right') {
    containerStyle += ' text-align: right;';
  }

  // Table styles for email compatibility
  let tableStyle = [
    'border-collapse: collapse',
    'border-spacing: 0',
    'font-family: Arial, sans-serif',
    'font-size: 14px',
    'line-height: 1.4',
    'color: #333333',
    width === 'w-full' ? 'width: 100%' :
    width === 'w-3/4' ? 'width: 75%' :
    width === 'w-1/2' ? 'width: 50%' :
    width === 'w-1/3' ? 'width: 33.333%' :
    width === 'w-1/4' ? 'width: 25%' : 'width: 100%'
  ].join('; ');

  // Border styles
  let cellStyle = 'padding: 8px 12px; vertical-align: top;';
  if (borderStyle === 'solid') {
    tableStyle += '; border: 1px solid #d1d5db';
    cellStyle += ' border: 1px solid #d1d5db;';
  } else if (borderStyle === 'dashed') {
    tableStyle += '; border: 1px dashed #d1d5db';
    cellStyle += ' border: 1px dashed #d1d5db;';
  }
  // Note: for 'none' borderStyle, we don't add any border styles

  let tableHTML = `<div style="${containerStyle}">
    <table style="${tableStyle}">`;

  // Header row
  if (headerRow && rows.length > 0) {
    tableHTML += '<thead><tr>';
    rows[0].forEach((cell: any) => {
      const headerStyle = `${cellStyle} background-color: #f9fafb; font-weight: 600;`;
      tableHTML += `<th style="${headerStyle}">${escapeHtmlText(cell.content || '')}</th>`;
    });
    tableHTML += '</tr></thead>';
  }

  // Body rows
  tableHTML += '<tbody>';
  const bodyRows = headerRow ? rows.slice(1) : rows;
  bodyRows.forEach((row: any) => {
    tableHTML += '<tr>';
    row.forEach((cell: any) => {
      tableHTML += `<td style="${cellStyle}">${escapeHtmlText(cell.content || '')}</td>`;
    });
    tableHTML += '</tr>';
  });
  tableHTML += '</tbody></table></div>';

  return tableHTML;
}

export function exportHTML(editorState: EditorState) {
  const contentState = editorState.getCurrentContent();
  let html = stateToHTML(contentState, options);

  const allDimensions = imageResizeStore.getAllDimensions();

  // Post-process HTML to replace video placeholders with email-friendly clickable images
  // Replace the entire figure/span structure containing video placeholders
  html = html.replace(/<figure><span[^>]*data-video-placeholder="VIDEO_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span><\/figure>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const alignment = data.alignment || 'left';

      // Use Google's static SVG icon for video preview
      // Extract filename from URL or use custom preview name
      const getFileName = (url: string) => {
        try {
          const urlParts = url.split('/');
          const fileNameWithQuery = urlParts[urlParts.length - 1];
          const fileName = fileNameWithQuery.split('?')[0]; // Remove query parameters
          return fileName || 'Video';
        } catch {
          return 'Video';
        }
      };

      const previewName = data.previewName || getFileName(data.src);

      // Build styles for alignment with proper video aspect ratio
      let containerStyle = 'margin: 8px 0;';

      if (alignment === 'center') {
        containerStyle += ' text-align: center;';
      } else if (alignment === 'right') {
        containerStyle += ' text-align: right;';
      }

      // Return simple text link with custom preview name
      return `<div style="${containerStyle}">
        <a href="${escapeHtmlAttr(data.src)}" target="_blank" rel="noopener noreferrer nofollow" style="color: #2563eb; text-decoration: underline; font-weight: 500; cursor: pointer;">
          ${escapeHtmlText(previewName)}
        </a>
      </div>`;
    } catch (e) {
      console.error('Video placeholder parsing error:', e);
      // If JSON parsing fails, return the original match
      return match;
    }
  });

  // Also handle standalone span tags (fallback)
  html = html.replace(/<span[^>]*data-video-placeholder="VIDEO_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const alignment = data.alignment || 'left';

      // Use Google's static SVG icon for video preview
      // Extract filename from URL or use custom preview name
      const getFileName = (url: string) => {
        try {
          const urlParts = url.split('/');
          const fileNameWithQuery = urlParts[urlParts.length - 1];
          const fileName = fileNameWithQuery.split('?')[0]; // Remove query parameters
          return fileName || 'Video';
        } catch {
          return 'Video';
        }
      };

      const previewName = data.previewName || getFileName(data.src);

      // Build styles for alignment with proper video aspect ratio
      let containerStyle = 'margin: 8px 0;';
      if (alignment === 'center') {
        containerStyle += ' text-align: center;';
      } else if (alignment === 'right') {
        containerStyle += ' text-align: right;';
      }

      // Return simple text link with custom preview name
      return `<div style="${containerStyle}">
        <a href="${escapeHtmlAttr(data.src)}" target="_blank" rel="noopener noreferrer nofollow" style="color: #2563eb; text-decoration: underline; font-weight: 500; cursor: pointer;">
          ${escapeHtmlText(previewName)}
        </a>
      </div>`;
    } catch (e) {
      console.error('Video placeholder parsing error:', e);
      return match;
    }
  });

  // Post-process HTML to replace CTA button placeholders with email-friendly buttons
  // Replace the entire figure/span structure containing CTA placeholders
  html = html.replace(/<figure><span[^>]*data-cta-placeholder="CTA_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span><\/figure>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const ctaData = data.data;

      // Email-friendly button styles with maximum compatibility
      const buttonStyle = [
        'display: inline-block',
        'text-decoration: none',
        'border-radius: 8px',
        'font-weight: 500',
        'text-align: center',
        'vertical-align: middle',
        'cursor: pointer',
        'padding: 12px 24px',
        'font-size: 14px',
        'line-height: 1.4',
        'border: 1px solid',
        `background-color: ${ctaData.backgroundColor}`,
        `color: ${ctaData.textColor}`,
        `border-color: ${ctaData.backgroundColor}`,
        // Email client compatibility styles
        'mso-padding-alt: 0',
        '-webkit-text-size-adjust: none',
        'mso-hide: all',
        // Outlook-specific fixes
        'mso-border-alt: none',
        'mso-style-priority: 99'
      ].join('; ');

      // Wrapper div for better email client compatibility with proper alignment
      const alignment = ctaData.alignment || 'center';
      const textAlign = alignment === 'left' ? 'left' : alignment === 'right' ? 'right' : 'center';
      const wrapperStyle = [
        `text-align: ${textAlign}`,
        'margin: 16px 0',
        'line-height: 1'
      ].join('; ');

      return `<div style="${wrapperStyle}">
        <a href="${escapeHtmlAttr(ctaData.url)}" target="_blank" rel="noopener noreferrer nofollow" style="${buttonStyle}">
          ${escapeHtmlText(ctaData.text)}
        </a>
      </div>`;
    } catch (e) {
      console.error('CTA placeholder parsing error:', e);
      // If JSON parsing fails, return the original match
      return match;
    }
  });

  // Also handle standalone span tags (fallback for CTA buttons)
  html = html.replace(/<span[^>]*data-cta-placeholder="CTA_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const ctaData = data.data;

      // Email-friendly button styles with maximum compatibility
      const buttonStyle = [
        'display: inline-block',
        'text-decoration: none',
        'border-radius: 8px',
        'font-weight: 500',
        'text-align: center',
        'vertical-align: middle',
        'cursor: pointer',
        'padding: 12px 24px',
        'font-size: 14px',
        'line-height: 1.4',
        'border: 1px solid',
        `background-color: ${ctaData.backgroundColor}`,
        `color: ${ctaData.textColor}`,
        `border-color: ${ctaData.backgroundColor}`,
        // Email client compatibility styles
        'mso-padding-alt: 0',
        '-webkit-text-size-adjust: none',
        'mso-hide: all',
        // Outlook-specific fixes
        'mso-border-alt: none',
        'mso-style-priority: 99'
      ].join('; ');

      // Wrapper div for better email client compatibility with proper alignment
      const alignment = ctaData.alignment || 'center';
      const textAlign = alignment === 'left' ? 'left' : alignment === 'right' ? 'right' : 'center';
      const wrapperStyle = [
        `text-align: ${textAlign}`,
        'margin: 16px 0',
        'line-height: 1'
      ].join('; ');

      return `<div style="${wrapperStyle}">
        <a href="${escapeHtmlAttr(ctaData.url)}" target="_blank" rel="noopener noreferrer nofollow" style="${buttonStyle}">
          ${escapeHtmlText(ctaData.text)}
        </a>
      </div>`;
    } catch (e) {
      console.error('CTA placeholder parsing error:', e);
      return match;
    }
  });

  // Post-process HTML to replace table placeholders with email-friendly tables
  html = html.replace(/<figure><span[^>]*data-table-placeholder="TABLE_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span><\/figure>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const tableData = data.data;

      return generateTableHTML(tableData);
    } catch (e) {
      console.error('Table placeholder parsing error:', e);
      return match;
    }
  });

  // Also handle standalone span tags (fallback for tables)
  html = html.replace(/<span[^>]*data-table-placeholder="TABLE_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const tableData = data.data;

      return generateTableHTML(tableData);
    } catch (e) {
      console.error('Table placeholder parsing error:', e);
      return match;
    }
  });

  // Post-process HTML to replace image dimensions and handle alignment
  if (Object.keys(allDimensions).length > 0) {
    // Find and replace image tags with resized dimensions
    html = html.replace(/<img([^>]*?)src="([^"]*?)"([^>]*?)>/g, (match, beforeSrc, src, afterSrc) => {
      const resizedDimensions = allDimensions[src];

      if (resizedDimensions) {
        // Extract alignment from existing style or data attributes
        let alignment = 'left';
        const fullMatch = beforeSrc + afterSrc;

        // Check for alignment in style attribute
        if (fullMatch.includes('margin: 0 auto') || fullMatch.includes('text-align: center')) {
          alignment = 'center';
        } else if (fullMatch.includes('margin-left: auto') || fullMatch.includes('text-align: right')) {
          alignment = 'right';
        }

        // Also check data-alignment attribute if present
        const alignmentMatch = fullMatch.match(/data-alignment="([^"]+)"/);
        if (alignmentMatch) {
          alignment = alignmentMatch[1];
        }

        // Remove existing width/height attributes and styles
        let newMatch = match
          .replace(/\s*width="[^"]*"/g, '')
          .replace(/\s*height="[^"]*"/g, '')
          .replace(/width:\s*\d+px\s*!important;?/g, '')
          .replace(/max-width:\s*\d+px\s*!important;?/g, '')
          .replace(/height:\s*\d+px\s*!important;?/g, '');

        // Add resized dimensions
        const { width, height } = resizedDimensions;

        // Add width/height attributes
        newMatch = newMatch.replace(/(<img[^>]*?)>/, `$1 width="${width}" height="${height}">`);

        // Clean up the image style to be minimal
        if (newMatch.includes('style="')) {
          newMatch = newMatch.replace(
            /style="([^"]*)"/,
            'style="display: block; border: 0; max-width: 100%; height: auto;"',
          );
        } else {
          newMatch = newMatch.replace(
            /(<img[^>]*?)>/,
            '$1 style="display: block; border: 0; max-width: 100%; height: auto;"',
          );
        }

        // Create alignment wrapper using simple div with text-align
        let wrapperStyle = '';

        if (alignment === 'center') {
          // For center alignment, use text-align center on wrapper div
          wrapperStyle = 'text-align: center; width: 100%;';
        } else if (alignment === 'right') {
          // For right alignment, use text-align right on wrapper div
          wrapperStyle = 'text-align: right; width: 100%;';
        } else {
          // For left alignment, default behavior
          wrapperStyle = 'text-align: left;';
        }

        // Create wrapper div with alignment and image container
        const alignmentWrapper = `<div style="${wrapperStyle}"><div style="width: ${width}px; height: ${height}px; overflow: hidden; display: inline-block;">`;
        const alignmentCloser = '</div></div>';

        newMatch = alignmentWrapper + newMatch + alignmentCloser;

        return newMatch;
      }

      return match;
    });
  }

  // Fix figure-wrapped images with alignment - remove figure tags around aligned images
  html = html.replace(/<figure>(<div[^>]*style="[^"]*text-align:[^"]*"[^>]*>.*?<\/div>).*?<\/figure>/gs, (divContent) => {
    // Return just the div content without the figure wrapper
    return divContent;
  });

  // Handle alignment for images that weren't resized
  html = html.replace(/<img([^>]*?)data-alignment="([^"]+)"([^>]*?)>/g, (match, alignment) => {
    // Skip if this image was already processed (wrapped in div)
    if (match.includes('width=') && match.includes('height=')) {
      // This image has dimensions, likely already processed
      return match;
    }

    // Extract image src for checking if it was in resized dimensions
    const srcMatch = match.match(/src="([^"]+)"/);
    if (srcMatch && allDimensions[srcMatch[1]]) {
      // This image was already handled in the resize section
      return match;
    }

    // Apply alignment wrapper for non-resized images using simple div
    if (alignment === 'center') {
      return `<div style="text-align: center; width: 100%;">${match}</div>`;
    } else if (alignment === 'right') {
      return `<div style="text-align: right; width: 100%;">${match}</div>`;
    } else {
      // For left alignment, no wrapper needed
      return match;
    }
  });

  // Post-process HTML to replace divider placeholders with email-friendly hr tags
  html = html.replace(/<figure><span[^>]*data-divider-placeholder="DIVIDER_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span><\/figure>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const dividerData = data;

      // Extract divider properties with defaults
      const style = dividerData.style || 'solid';
      const color = dividerData.color || '#cccccc';
      const thickness = dividerData.thickness || 1;
      const alignment = dividerData.alignment || 'center';
      const width = dividerData.width || '100%';

      // For email compatibility, use a table with a border-top
      let marginLeft = '0';
      let marginRight = '0';

      if (alignment === 'center') {
        marginLeft = 'auto';
        marginRight = 'auto';
      } else if (alignment === 'right') {
        marginLeft = 'auto';
      } else if (alignment === 'left') {
        marginRight = 'auto';
      }

      // Use a simple div with border-top for email compatibility
      // Most email clients support this better than hr tags
      const divStyle = [
        'border: 0',
        `border-top: ${thickness}px ${style} ${color}`,
        `width: ${width}`,
        `margin: 16px ${marginRight} 16px ${marginLeft}`,
        'padding: 0',
        'height: 0',
        'line-height: 0',
        'font-size: 0'
      ].join('; ');

      return `<div style="${divStyle}"></div>`;
    } catch (e) {
      console.error('Divider placeholder parsing error:', e);
      return match;
    }
  });

  // Also handle standalone span tags (fallback for dividers)
  html = html.replace(/<span[^>]*data-divider-placeholder="DIVIDER_PLACEHOLDER_[^"]*_DATA_([^"]+)_END"[^>]*>[^<]*<\/span>/g, (match, encodedDataJson) => {
    try {
      // Decode HTML entities
      const decodedJson = encodedDataJson
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const data = JSON.parse(decodedJson);
      const dividerData = data;

      // Extract divider properties with defaults
      const style = dividerData.style || 'solid';
      const color = dividerData.color || '#cccccc';
      const thickness = dividerData.thickness || 1;
      const alignment = dividerData.alignment || 'center';
      const width = dividerData.width || '100%';

      // For email compatibility, use a table with a border-top
      let marginLeft = '0';
      let marginRight = '0';

      if (alignment === 'center') {
        marginLeft = 'auto';
        marginRight = 'auto';
      } else if (alignment === 'right') {
        marginLeft = 'auto';
      } else if (alignment === 'left') {
        marginRight = 'auto';
      }

      // Use a simple div with border-top for email compatibility
      // Most email clients support this better than hr tags
      const divStyle = [
        'border: 0',
        `border-top: ${thickness}px ${style} ${color}`,
        `width: ${width}`,
        `margin: 16px ${marginRight} 16px ${marginLeft}`,
        'padding: 0',
        'height: 0',
        'line-height: 0',
        'font-size: 0'
      ].join('; ');

      return `<div style="${divStyle}"></div>`;
    } catch (e) {
      console.error('Divider placeholder parsing error:', e);
      return match;
    }
  });

  return html;
}
